/**
 * The contract for `GET /api/v1/dashboard/summary`.
 *
 * One endpoint, one round trip, shaped by the caller's role. The dashboard used
 * to assemble itself from six parallel queries — two of which pulled 100 rows
 * apiece only to read `meta.total` off the envelope — and then showed every
 * role the same four cards. Roles do different jobs, so they get different
 * payloads; the envelope below is the union, and a block that does not apply to
 * a role comes back empty rather than absent, so the client needs no narrowing.
 *
 * Server-side shaping is not cosmetic. An interviewer has `jobs.view: false` and
 * `offers.view: false`, so "10 open jobs" is not merely irrelevant to them, it
 * is data they may not have. Deciding that here means the numbers never reach
 * the browser in the first place.
 */
import { HIRE_SYSTEM_ROLES } from "../roles";
import type { DataScope } from "./accessScope";

/**
 * The four dashboards, keyed by what the person actually does rather than by
 * role name (decision 10): the role's data scope says which slice of hiring it
 * reaches, and that decides the dashboard.
 *
 *   all                    → admin         (admin, platform, legacy super_admin)
 *   assigned_jobs          → hiring_manager (limited team member: a different job, not a smaller one)
 *   communities            → recruiter     (team member and every custom role)
 *   assigned_applications  → interviewer
 *   client_shares, none    → no dashboard  (client, employee have their own landing pages)
 *
 * A scoped dashboard also needs the applicant permission, since every block on
 * it counts candidates. A caller with no role in the instance gets none, as
 * before.
 *
 * Accepts the role's name too, for the client's fallback before the summary
 * loads: a system role name resolves through the catalog; anything else is null
 * there, and the server's answer (which knows the data scope) decides.
 */
export type DashboardRole = "interviewer" | "hiring_manager" | "recruiter" | "admin";

export interface DashboardRoleInput {
  dataScope: DataScope | null | undefined;
  permissions?: Record<string, boolean> | null;
  noRoleAssignment?: boolean;
}

export function dashboardRoleFor(
  input: DashboardRoleInput | string | null | undefined,
): DashboardRole | null {
  if (input === null || input === undefined || input === "") return null;
  if (typeof input === "string") {
    // Hire only: these are the hire dashboards; a performance role name has none (null, as before).
    const role = HIRE_SYSTEM_ROLES.find((r) => r.name === input);
    if (!role) return null;
    return dashboardRoleFor({
      dataScope: role.dataScope,
      permissions: role.permissions as Record<string, boolean>,
    });
  }
  if (input.noRoleAssignment) return null;
  if (input.dataScope === "all") return "admin";
  const canSeeApplicants = input.permissions?.["applicants.view"] === true;
  if (!canSeeApplicants) return null;
  switch (input.dataScope) {
    case "assigned_applications":
      return "interviewer";
    case "assigned_jobs":
      return "hiring_manager";
    case "communities":
      return "recruiter";
    default:
      return null;
  }
}

/**
 * The window the flow measures are counted over.
 *
 * Applies to what *happened* — applications received, the conversion figures,
 * the activity chart, the review queue. It deliberately does NOT apply to work
 * queues: feedback owed on an interview from five months ago still needs
 * writing, upcoming interviews are already a forward window, and an open
 * requisition is a current state rather than an event. Filtering those by date
 * would quietly shrink the backlog on screen without shrinking the real one.
 */
export type DashboardPeriod = "1d" | "7d" | "30d" | "90d" | "all";

export const DASHBOARD_PERIODS: { value: DashboardPeriod; label: string }[] = [
  { value: "1d", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time" },
];

/** Days in a period. `all` has no start date, so it has no length. */
export const PERIOD_DAYS: Record<Exclude<DashboardPeriod, "all">, number> = {
  "1d": 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

/**
 * The Scheduled Interviews filters a dashboard number can open on.
 *
 * `today` is the page's own Today card. The two feedback filters have no card
 * there; they exist so "Chase feedback on 9 interviews" opens those nine.
 * Each is one server-side predicate (statCardPredicate in
 * server/routes/interviews.ts) that the dashboard counts with too.
 */
export type InterviewListFilter = "today" | "feedback_owed" | "feedback_outstanding";

/** Scheduled Interviews, filtered to exactly what a dashboard number counted. */
export function interviewListLink(filter: InterviewListFilter): string {
  return `/scheduled-tours?statCard=${filter}`;
}

/** Scheduled Interviews in its calendar, on the week containing today. */
export const INTERVIEW_CALENDAR_WEEK_LINK = "/scheduled-tours?view=calendar&calendarView=week";

/**
 * Open roles on the Jobs page. "Published" there is `is_published` and not
 * expired, the same definition the requisitions card counts (OPEN_JOB).
 */
export const OPEN_ROLES_LINK = "/jobs?status=published";

/** "1 interview", "2 interviews". Every count the dashboard prints goes through here. */
export function countOf(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Drives the accent colour only. Never the sole carrier of meaning. */
export type DashboardTone = "neutral" | "positive" | "attention" | "critical";

export interface DashboardMetric {
  key: string;
  label: string;
  value: number;
  /** Second line under the number, e.g. "3 overdue". Omitted when there is nothing to add. */
  sublabel?: string;
  tone: DashboardTone;
  /** Client route to open on click, already role-checked server-side. */
  to?: string;
}

/**
 * One ranked thing to do next.
 *
 * `priority` is a plain number so the server can rank across kinds — feedback
 * that blocks an offer outranks a stale requisition — and the client can render
 * the list without re-deriving the order.
 */
export interface DashboardAction {
  id: string;
  kind:
    | "feedback_owed"
    | "feedback_chase"
    | "interview_unscheduled"
    | "triage_backlog"
    | "requisition_stalled"
    | "candidate_cold"
    /** Admins only: the HRIS roster has not loaded new data within the stale window. */
    | "hris_roster_stale";
  priority: number;
  title: string;
  detail: string;
  count?: number;
  tone: DashboardTone;
  /**
   * Where the row goes. No `cta` label any more: the rows are clickable end to
   * end, so a button repeating what the row already means was both a smaller
   * target and, four to a card, a row of unrelated-looking choices instead of
   * one ranked queue. An action with no `to` is handled by the caller.
   */
  to?: string;
}

export interface DashboardInterview {
  id: string;
  scheduledAt: string;
  durationMinutes: number | null;
  interviewType: string | null;
  status: string;
  // No `meetingUrl`. The dashboard row opens the candidate and nothing else,
  // so carrying the join link would be payload nobody reads. Restore the
  // column in the upcoming-interviews query if a Join affordance comes back.
  applicationId: string | null;
  jobId: string | null;
  candidateName: string | null;
  jobTitle: string | null;
  /** Panel names, for the roles allowed to see who else is on the call. */
  panel: string[];
}

export interface FeedbackOwedItem {
  interviewId: string;
  applicationId: string | null;
  scheduledAt: string;
  daysOverdue: number;
  candidateName: string | null;
  jobTitle: string | null;
  /** Whose feedback is missing. Empty for "mine" — the caller already knows. */
  pendingFrom: string[];
}

export interface RequisitionRow {
  jobId: string;
  title: string;
  jobCode: string | null;
  department: string | null;
  locationName: string | null;
  daysOpen: number;
  totalApplicants: number;
  /**
   * Candidates on this role in the Lead stage now, whenever they applied. Not
   * "applied recently": a stalled role can hold eleven unreviewed leads, which
   * is why the card heads this column "Lead" and not "New".
   */
  newApplicants: number;
  inInterview: number;
  inOffer: number;
  /** Null when the requisition has never had an application. */
  lastApplicationAt: string | null;
  daysSinceLastApplication: number | null;
}

export interface TriageCandidate {
  applicationId: string;
  fullName: string | null;
  email: string | null;
  jobId: string | null;
  jobTitle: string | null;
  submittedAt: string | null;
  /** 0-100 from the cached `match_score` jsonb. Null when never scored. */
  matchScore: number | null;
  matchRecommendation: string | null;
  daysWaiting: number;
}

/**
 * One bar of the pipeline chart: how many candidates are in this stage *now*.
 *
 * `label` is the stage category exactly as the Applicants page names it — Lead,
 * Applicant, Interview, Offer, Hired. Nothing here is invented: an earlier
 * version printed "New" and "In review", which are not statuses this product
 * has, so a number on the dashboard could not be reconciled with any screen the
 * user could open.
 */
export interface FunnelStage {
  key: string;
  label: string;
  value: number;
  /** Share of everyone currently in the pipeline, 0-100. */
  percent: number;
}

/**
 * The actual conversion figures, kept apart from the bars.
 *
 * The bars are a snapshot — candidates leave a stage as well as enter it, so
 * "interview" can hold more people than "applicant" and the bars are not, and
 * cannot be, a funnel. Conversion is the cumulative question ("of everyone who
 * applied, how many were hired"), answered from `application_status_history`,
 * so it gets its own field rather than being forced onto a shape that would
 * misreport it.
 */
export interface ConversionSummary {
  applied: number;
  reviewed: number;
  interviewed: number;
  offered: number;
  hired: number;
  /** Hired as a share of applied, 0-100. */
  hireRate: number;
}

/**
 * One day on the activity chart.
 *
 * Carried in the summary rather than fetched from `/analytics/timeseries`
 * separately: it is the same instance, the same role scoping and the same
 * render pass, so a second round trip would buy nothing but a second spinner.
 */
export interface TrendPoint {
  /** The day being counted, "YYYY-MM-DD", cut in the viewer's display zone. */
  date: string;
  applications: number;
  interviews: number;
  /** Candidates whose status moved into a hired stage that day. */
  hired: number;
}

/**
 * The full size of each list above, which the payload caps.
 *
 * Every count the dashboard shows for a list comes from here, never from the
 * length of the capped array: a card whose badge stopped at the cap would
 * disagree with the page it links to as soon as the desk got busy.
 */
export interface DashboardTotals {
  /** `interviews`: scheduled or confirmed, two hours ago to seven days ahead. */
  interviews: number;
  feedbackOwed: number;
  /** Of `feedbackOwed`, the ones two or more days past the interview. */
  feedbackOwedOverdue: number;
  feedbackChase: number;
  /** Open roles in the caller's scope. */
  requisitions: number;
  /** Of `requisitions`, the ones with no application in seven days. */
  requisitionsStale: number;
}

export interface DashboardSummary {
  role: DashboardRole;
  roleName: string;
  generatedAt: string;
  /** The window the flow measures were counted over. Echoed back so the UI
   *  labels match what the server actually applied. */
  period: DashboardPeriod;
  /** Days the activity chart covers. Floored at 7 — a one-day "trend" is a
   *  single dot, so "Today" still charts a week and says so. */
  trendDays: number;
  /**
   * The period in words ("last 7 days"), for labels and empty states.
   *
   * Empty states must name the window or they lie by omission: an instance
   * with 100 unreviewed leads, none of them from this week, would otherwise
   * say "No new candidates waiting" — which reads as an empty pipeline rather
   * than an empty week.
   */
  periodLabel: string;
  /**
   * The same window as a phrase that follows a verb: "in the last 7 days",
   * "today", "all time".
   *
   * Separate from `periodLabel` because one string cannot do both jobs —
   * templating "in the {label}" produced "applied in the all time" and
   * "Nobody applied in the today". The label is the bare noun for chips and
   * sublabels; the phrase carries its own preposition, or none where English
   * does not want one.
   */
  periodPhrase: string;
  metrics: DashboardMetric[];
  actions: DashboardAction[];
  interviews: DashboardInterview[];
  feedbackOwed: FeedbackOwedItem[];
  feedbackChase: FeedbackOwedItem[];
  requisitions: RequisitionRow[];
  triage: TriageCandidate[];
  funnel: FunnelStage[];
  conversion: ConversionSummary | null;
  totals: DashboardTotals;
  trend: TrendPoint[];
  /**
   * Parts that could not be read this time, left out rather than shown as zero. `"feedback"`:
   * who can give feedback could not be read, so the feedback tiles, lists and actions are absent
   * and `feedbackOwed`/`feedbackChase` are empty. Absent when everything loaded.
   */
  unavailable?: DashboardUnavailablePart[];
}

/** A part of the summary that can fail on its own (see DashboardSummary.unavailable). */
export type DashboardUnavailablePart = "feedback";
